import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Home, Loader2 } from "lucide-react";
import Logo from "@/components/Logo";

export default function Register() {
  const { register, formatApiError } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(email, password, name);
      toast.success(`Welcome home, ${name}.`);
      nav("/", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-hearth-cream">
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md fade-up">
          <Link to="/" className="inline-block mb-10">
            <Logo size={40} showWord />
          </Link>

          <p className="caption text-hearth-mute mb-3">Create your home</p>
          <h2 className="font-display text-4xl font-semibold tracking-tight text-hearth-ink">
            A fresh new household.
          </h2>
          <p className="text-hearth-mute mt-2">
            You&apos;ll get an invite code afterwards to bring others in.
          </p>

          <form onSubmit={submit} className="mt-10 space-y-5">
            <div>
              <Label className="caption text-hearth-mute" htmlFor="name">Your name</Label>
              <Input
                id="name"
                data-testid="register-name-input"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input-warm mt-2 h-12"
                placeholder="Maya"
              />
            </div>
            <div>
              <Label className="caption text-hearth-mute" htmlFor="email">Email</Label>
              <Input
                id="email"
                data-testid="register-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-warm mt-2 h-12"
                placeholder="you@home.com"
              />
            </div>
            <div>
              <Label className="caption text-hearth-mute" htmlFor="password">Password</Label>
              <Input
                id="password"
                data-testid="register-password-input"
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-warm mt-2 h-12"
                placeholder="At least 6 characters"
              />
            </div>
            <Button
              data-testid="register-submit-button"
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-full bg-hearth-terracotta hover:bg-hearth-terracottaDark text-white font-medium transition-transform hover:-translate-y-0.5 active:scale-[0.98]"
            >
              {loading ? <Loader2 className="animate-spin" size={18}/> : "Create household"}
            </Button>
          </form>

          <p className="mt-8 text-sm text-hearth-mute">
            Already have an account?{" "}
            <Link to="/login" data-testid="register-login-link" className="text-hearth-terracotta font-medium underline-offset-4 hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </div>
      <div className="hidden lg:flex w-1/2 relative overflow-hidden">
        <img
          src="https://images.pexels.com/photos/10388479/pexels-photo-10388479.jpeg"
          alt="monstera plant in sunlight"
          className="object-cover w-full h-full"
        />
        <div className="absolute inset-0 bg-gradient-to-bl from-hearth-ink/40 via-transparent to-transparent" />
      </div>
    </div>
  );
}
